import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  FooterContentPosition,
  ajvResolver,
  usePrevious,
  responseErrorsResolver,
  ResponseError,
  Popup,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearCreateBlacklistState, onSubmit } from './store/slices/create';
import BlacklistCreateForm from '../../components/blacklist/create';
import {
  useCreateBlacklistMutation,
  useUpdateBlacklistSettingsMutation,
  useLazyGetBlacklistsQuery,
} from './store/services';
import { Blacklist, BlacklistFormValues, BlacklistSettings } from '../types';
import enableAll from './helpers/enable-all';

import ROUTES from '../../routes';

import cn from './blacklist.module.css';

const defaultSettingsValues = {
  corporateClients: false,
  corporateClientsAdverseMedia: false,
  corporateClientsEnforcement: false,
  corporateClientsPEP: false,
  corporateClientsSOESWF: false,
  corporateClientsSanction: false,
  individualClients: false,
  individualClientsAdverseMedia: false,
  individualClientsEnforcement: false,
  individualClientsPEP: false,
  individualClientsSOESWF: false,
  individualClientsSanction: false,
  paymentOrderPayersPayees: false,
  paymentOrderPayersPayeesAdverseMedia: false,
  paymentOrderPayersPayeesEnforcement: false,
  paymentOrderPayersPayeesPEP: false,
  paymentOrderPayersPayeesSOESWF: false,
  paymentOrderPayersPayeesSanction: false,
  transactionsCorporateClients: false,
  transactionsCorporateClientsAdverseMedia: false,
  transactionsCorporateClientsEnforcement: false,
  transactionsCorporateClientsPEP: false,
  transactionsCorporateClientsSOESWF: false,
  transactionsCorporateClientsSanction: false,
  transactionsCorporatePayersPayees: false,
  transactionsCorporatePayersPayeesAdverseMedia: false,
  transactionsCorporatePayersPayeesEnforcement: false,
  transactionsCorporatePayersPayeesPEP: false,
  transactionsCorporatePayersPayeesSOESWF: false,
  transactionsCorporatePayersPayeesSanction: false,
  transactionsCounterpartiesBanks: false,
  transactionsCounterpartiesBanksAdverseMedia: false,
  transactionsCounterpartiesBanksEnforcement: false,
  transactionsCounterpartiesBanksPEP: false,
  transactionsCounterpartiesBanksSOESWF: false,
  transactionsCounterpartiesBanksSanction: false,
  transactionsIndividualClients: false,
  transactionsIndividualClientsAdverseMedia: false,
  transactionsIndividualClientsEnforcement: false,
  transactionsIndividualClientsPEP: false,
  transactionsIndividualClientsSOESWF: false,
  transactionsIndividualClientsSanction: false,
  transactionsIndividualPayersPayees: false,
  transactionsIndividualPayersPayeesAdverseMedia: false,
  transactionsIndividualPayersPayeesEnforcement: false,
  transactionsIndividualPayersPayeesPEP: false,
  transactionsIndividualPayersPayeesSOESWF: false,
  transactionsIndividualPayersPayeesSanction: false,
};

const defaultSettings = {
  name: '',
  description: '',
  source: '',
  ...defaultSettingsValues,
};

const schema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Name is required',
    },
  },
  required: ['name'],
};

const BlacklistCreateHOC = ({ type }: { type: 'results' | 'users' }) => {
  const {
    create: { requestInProgress, createSuccess },
  } = useAppSelector((state) => state.blacklist);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const [formattedSchema, setFormattedSchema] = useState(schema);

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [allSettingsEnabled, setAllSettingsEnabled] = useState(false);
  const [settings, setSettings] = useState<BlacklistSettings>(defaultSettings);
  const [globalError, setGlobalError] = useState('');

  const { active } = useOutletContext<{
    active: boolean;
  }>();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset,
    setError,
    watch,
    setValue,
  } = useForm<BlacklistFormValues>({
    resolver: ajvResolver(formattedSchema),
    defaultValues: defaultSettings,
  });

  const description = watch('description');
  const prevDescription = usePrevious(description);

  const fields = watch();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [createBlacklist] = useCreateBlacklistMutation();
  const [updateBlacklistSettings] = useUpdateBlacklistSettingsMutation();
  const [getBlacklists] = useLazyGetBlacklistsQuery();

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');
    dispatch(onSubmit(values));

    const body: {
      name: string;
      description?: string;
    } = {
      name: values.name,
    };

    if (values.description) {
      body.description = values.description;
    }

    const response = (await createBlacklist(body)) as {
      data?: { data: Blacklist };
      error?: ResponseError;
    };

    if (!response.error) {
      getBlacklists(
        `?take=20&sortDir=desc&sortProp=createdAt${
          active ? `&active=${active}` : ''
        }`,
      );

      await updateBlacklistSettings({
        id: response.data?.data.id,
        body: {
          corporateClients: values?.corporateClients,
          corporateClientsAdverseMedia: values?.corporateClientsAdverseMedia,
          corporateClientsEnforcement: values?.corporateClientsEnforcement,
          corporateClientsPEP: values?.corporateClientsPEP,
          corporateClientsSOESWF: values?.corporateClientsSOESWF,
          corporateClientsSanction: values?.corporateClientsSanction,
          individualClients: values?.individualClients,
          individualClientsAdverseMedia: values?.individualClientsAdverseMedia,
          individualClientsEnforcement: values?.individualClientsEnforcement,
          individualClientsPEP: values?.individualClientsPEP,
          individualClientsSOESWF: values?.individualClientsSOESWF,
          individualClientsSanction: values?.individualClientsSanction,
          paymentOrderPayersPayees: values?.paymentOrderPayersPayees,
          paymentOrderPayersPayeesAdverseMedia:
            values?.paymentOrderPayersPayeesAdverseMedia,
          paymentOrderPayersPayeesEnforcement:
            values?.paymentOrderPayersPayeesEnforcement,
          paymentOrderPayersPayeesPEP: values?.paymentOrderPayersPayeesPEP,
          paymentOrderPayersPayeesSOESWF:
            values?.paymentOrderPayersPayeesSOESWF,
          paymentOrderPayersPayeesSanction:
            values?.paymentOrderPayersPayeesSanction,
          transactionsCorporateClients: values?.transactionsCorporateClients,
          transactionsCorporateClientsAdverseMedia:
            values?.transactionsCorporateClientsAdverseMedia,
          transactionsCorporateClientsEnforcement:
            values?.transactionsCorporateClientsEnforcement,
          transactionsCorporateClientsPEP:
            values?.transactionsCorporateClientsPEP,
          transactionsCorporateClientsSOESWF:
            values?.transactionsCorporateClientsSOESWF,
          transactionsCorporateClientsSanction:
            values?.transactionsCorporateClientsSanction,
          transactionsCorporatePayersPayees:
            values?.transactionsCorporatePayersPayees,
          transactionsCorporatePayersPayeesAdverseMedia:
            values?.transactionsCorporatePayersPayeesAdverseMedia,
          transactionsCorporatePayersPayeesEnforcement:
            values?.transactionsCorporatePayersPayeesEnforcement,
          transactionsCorporatePayersPayeesPEP:
            values?.transactionsCorporatePayersPayeesPEP,
          transactionsCorporatePayersPayeesSOESWF:
            values?.transactionsCorporatePayersPayeesSOESWF,
          transactionsCorporatePayersPayeesSanction:
            values?.transactionsCorporatePayersPayeesSanction,
          transactionsCounterpartiesBanks:
            values?.transactionsCounterpartiesBanks,
          transactionsCounterpartiesBanksAdverseMedia:
            values?.transactionsCounterpartiesBanksAdverseMedia,
          transactionsCounterpartiesBanksEnforcement:
            values?.transactionsCounterpartiesBanksEnforcement,
          transactionsCounterpartiesBanksPEP:
            values?.transactionsCounterpartiesBanksPEP,
          transactionsCounterpartiesBanksSOESWF:
            values?.transactionsCounterpartiesBanksSOESWF,
          transactionsCounterpartiesBanksSanction:
            values?.transactionsCounterpartiesBanksSanction,
          transactionsIndividualClients: values?.transactionsIndividualClients,
          transactionsIndividualClientsAdverseMedia:
            values?.transactionsIndividualClientsAdverseMedia,
          transactionsIndividualClientsEnforcement:
            values?.transactionsIndividualClientsEnforcement,
          transactionsIndividualClientsPEP:
            values?.transactionsIndividualClientsPEP,
          transactionsIndividualClientsSOESWF:
            values?.transactionsIndividualClientsSOESWF,
          transactionsIndividualClientsSanction:
            values?.transactionsIndividualClientsSanction,
          transactionsIndividualPayersPayees:
            values?.transactionsIndividualPayersPayees,
          transactionsIndividualPayersPayeesAdverseMedia:
            values?.transactionsIndividualPayersPayeesAdverseMedia,
          transactionsIndividualPayersPayeesEnforcement:
            values?.transactionsIndividualPayersPayeesEnforcement,
          transactionsIndividualPayersPayeesPEP:
            values?.transactionsIndividualPayersPayeesPEP,
          transactionsIndividualPayersPayeesSOESWF:
            values?.transactionsIndividualPayersPayeesSOESWF,
          transactionsIndividualPayersPayeesSanction:
            values?.transactionsIndividualPayersPayeesSanction,
        },
      });

      navigate(ROUTES.BLACKLIST_LIST_RESULTS);
    } else {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        type === 'results'
          ? ROUTES.BLACKLIST_LIST_RESULTS
          : ROUTES.BLACKLIST_LIST_USERS,
      );
    }, 100);
  }, []);

  useEffect(() => {
    let nextSchema: any = formattedSchema;

    if (description && !prevDescription) {
      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
          description: {
            minLength: 2,
            type: 'string',
          },
        },
        required: [...nextSchema.required, 'description'],
      };
    } else if (!description && prevDescription) {
      delete nextSchema.properties.description;

      nextSchema = {
        ...nextSchema,
        required: nextSchema.required.filter(
          (item: string) => item !== 'description',
        ),
      };
    }

    setFormattedSchema(nextSchema);
  }, [description, prevDescription]);

  useEffect(() => {
    if (createSuccess) {
      handleClose();
    }
  }, [createSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearCreateBlacklistState());
    };
  }, [dispatch]);

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => (isDirty ? setDirtyPopupVisible(true) : handleClose())}
        title="Add List"
        sidepanelExpandedKey="blacklist-add"
        footer={
          <ButtonPrimary
            text={requestInProgress ? 'Loading...' : 'Add List'}
            className={cn.createBlacklistSubmit}
            onClick={onFormSubmit}
            bold
          />
        }
        errorSidepanel={globalError}
        footerContentPosition={FooterContentPosition.Center}
      >
        <BlacklistCreateForm
          control={control}
          errors={errors}
          setAllSettingsEnabled={setAllSettingsEnabled}
          fields={fields}
          setValue={setValue}
          setSettings={setSettings}
          settings={settings}
          enableAll={() =>
            enableAll({
              getValues,
              defaultSettings: defaultSettingsValues,
              allSettingsEnabled,
              setAllSettingsEnabled,
              reset,
              setSettings,
            })
          }
          allSettingsEnabled={allSettingsEnabled}
        />
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default BlacklistCreateHOC;
